import {getLoginReturnUrl, setLoginReturnUrl} from './login-return-url';
import { CredentialContext, CredentialRefresh } from './credential-transition';
import { Inject, Injectable } from '@angular/core';
import { ApiService } from '../api/api.service';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Dictionary } from 'highcharts';
import { ApiClient } from '../http/api-client';
import { AppSettings } from '../../app.settings';
import { RESTANGULAR_INTEGRATION_SERVER } from '../../integration-restangular.token';
import { HttpClient } from '@angular/common/http';
import {CognitoMfaService} from '../cognito/cognito-mfa.service';
import {Router} from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TabStorageService } from '../tabstorage/tabstorage.service';
import { SnackBarComponent } from '../../components/snackbar/snackbar.component';
import {
    ADMIN_HANDOFF_VERSION,
    buildHandoffUrl,
    buildLegacyHandoffUrl,
    handoffFailureCategory,
    HANDOFF_FAILURE_MESSAGE,
    HandoffWindowBlockedError,
    InvalidHandoffResponseError,
    parseAdminHandoffResponse,
} from '../../helpers/handoff-url.helper';

@Injectable({
    providedIn: 'root'
})
export class SessionService {
    private static readonly ABSOLUTE_SESSION_EXPIRATION_CLAIM = 'sessionAbsoluteExp';

    JWT_TOKEN = 'JWTToken';
    REFRESH_TOKEN = 'refreshToken';

    public get loginUrl(): string | null { return getLoginReturnUrl(); }
    public set loginUrl(value: string | null) { setLoginReturnUrl(value); }

    async navigateAfterLogin(): Promise<boolean> {
        const destination = this.loginUrl;
        const navigated = await this.router.navigateByUrl(destination || '/', {replaceUrl: true});
        if (navigated && this.loginUrl === destination) this.loginUrl = null;
        return navigated;
    }

    public createTeam = false;

    private isIntegrationAdmin = AppSettings.isIntegration();
    private refreshCoordinator: CredentialRefresh;

    constructor(
        private apiService: ApiService,
        private restangular: ApiClient,
        private tabStorageService: TabStorageService,
        @Inject(RESTANGULAR_INTEGRATION_SERVER) private integrationRestangular: ApiClient,
        private http: HttpClient,
        private cognitoMFAService: CognitoMfaService,
        private router: Router,
        private snackBar: MatSnackBar
    ) {
        this.refreshCoordinator = new CredentialRefresh(this.tabStorageService.credentials, token => this.apiService.setToken(token));
    }

  login(userId: string): Promise<any> {
    // Numeric v2 requires an explicit opaque or legacy selection.
    // Mixed rolling deployments fail closed because old untagged responses are rejected below.
    return this.apiService.post(`admin/userLogin/${userId}`, {handoffVersion: ADMIN_HANDOFF_VERSION});
  }

    // Callers launch this fire-and-forget from a menu, so failures are surfaced here.
    loginByUserId(userId: string): Promise<void> {
        return this.login(userId)
            .then(data => this.openHandoff(data))
            .catch(error => this.reportHandoffFailure(error));
    }

    // Rejects on failure so the caller can name the team in its own error message.
    loginTeamAgentByTeamId(teamId: string, companyId: string): Promise<void> {
        return this.apiService.post(`admin/teamAgentLogin/${teamId}`, {
            companyId,
            handoffVersion: ADMIN_HANDOFF_VERSION,
        }).then(data => {
            this.openHandoff(data);
        });
    }

    isTeamAgentSession(): boolean {
        const token = this.decodeJWTToken();
        return token?.teamAgent === true
            || token?.teamagent === true
            || token?.info?.teamAgent === true
            || token?.info?.teamagent === true
            || token?.role === 'teamagent'
            || token?.info?.role === 'teamagent';
    }

    // Legacy is valid only when explicitly selected by a numeric-v2 response. Parsing or opening an
    // opaque selection can fail only as an error; this method never catches it and retries legacy.
    private openHandoff(data: any): void {
        const selection = parseAdminHandoffResponse(data);
        if (!selection) {
            throw new InvalidHandoffResponseError();
        }

        const url = selection.mode === 'opaque'
            ? buildHandoffUrl(window.location.origin, selection.handoff, {api: this.apiService.apiName})
            : buildLegacyHandoffUrl(window.location.origin, selection.legacy, {api: this.apiService.apiName});
        if (!window.open(url, '_blank')) {
            throw new HandoffWindowBlockedError();
        }
    }

    // Never echoes the response: a legacy backend can still return credential-bearing fields.
    private reportHandoffFailure(error: any): void {
        const category = handoffFailureCategory(error);
        console.error('Application handoff failed.', {category});
        this.snackBar.openFromComponent(SnackBarComponent, {data: HANDOFF_FAILURE_MESSAGE});
    }

    async loginUserPassword(email: string, password: string): Promise<any> {
        const user: {username: string, password: string} = {username: email, password};
        if (this.isIntegrationAdmin) {
            return this.integrationRestangular.one('auth/user').customPOST({ user_email: email, user_password: password }).toPromise().then(async (result: Dictionary<any>) => {
                this.setToken(result.session_token, result.refresh_token);
                await this.cognitoMFAService.saveCognitoRefreshTokenInformation();
                return result;
            });
        } else {
            return this.apiService.post('auth/user-password', { username: email, password }).then(async data => {

                await this.cognitoAuthMethods(user).then(() => {

                }).catch(err =>  {
                    console.error('Error in cognito auth method: ', err);
                    return Promise.reject(`'Error in cognito auth method: ', ${err}`);
                });

                const parts = data.url.split('/').slice(-2);
                this.setToken(parts[0], parts[1]);
                await this.cognitoMFAService.saveCognitoRefreshTokenInformation();
                return data;
            });
        }

    }

    async cognitoAuthMethods(user: {username: string, password: string}): Promise<void> {
        try {
            const cognitoUserResponse: any = await this.cognitoMFAService.cognitoMFASignIn(user);
            if (cognitoUserResponse?.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
                void this.router.navigate(['2fa/app/add-code', user.username, 'secret'], {state: user});
                return Promise.reject('SOFTWARE_TOKEN_MFA Required');
            }
            if (!cognitoUserResponse?.isSignedIn) throw new Error('Cognito sign-in was not completed.');
        } catch (error) {
            return Promise.reject(error);
        }
    }

    public loginValidateCode(phoneNumber: string, code: string): Promise<any> {
        return this.http.post(AppSettings.getEndpoint() + `auth/mobile/${phoneNumber}/verificationCode/${code}`, {}).toPromise().then(async (data: any) => {
            this.parseTokens(data);
            await this.cognitoMFAService.saveCognitoRefreshTokenInformation();
            return data;
        });
    }

    logout(): void {
        this.loginUrl = null;
        this.tabStorageService.clearCurrentTabSession();
        this.apiService.unsetToken();
    }

    private parseTokens(data: any): void {
        const parts = data.id.split('/').slice(-2);
        this.setToken(parts[0], parts[1]);
    }

    public setToken(token: string, refreshToken: string | null = null): void {
        this.tabStorageService.credentials.replace({token, refreshToken});
        this.apiService.setToken(token);
    }

    public consumeHandoff(handoffCode: string): Promise<any> {
        return this.http.post<any>(
            `${AppSettings.getEndpoint().replace(/\/?$/, '/')}auth/federated/handoff`,
            {handoffCode},
        ).toPromise();
    }

    public captureSessionContext(): () => boolean {
        return this.tabStorageService.credentials.capture().current;
    }

    public captureCredentialContext(): CredentialContext {
        return this.tabStorageService.credentials.capture();
    }

    public refreshToken(isCurrent: () => boolean = () => true, request?: CredentialContext): Promise<any> {
        return this.refreshCoordinator.run(isCurrent, request);
    }

    deletePhoneNumber(tel: string): Promise<any> {
        return this.restangular.one('mobile', tel).remove().toPromise();
    }

    public decodeJWTToken(jwt: string | null = this.tabStorageService.getStorage(this.JWT_TOKEN)): Dictionary<any> | null {
        if (!jwt) {
            return null;
        }
        try {
            const helper = new JwtHelperService();
            return helper.decodeToken(jwt);
        } catch (err) {
            return null;
        }
    }

    public isAbsoluteSessionExpired(jwt: string | null = this.tabStorageService.getStorage(this.JWT_TOKEN)): boolean {
        const decodedToken = this.decodeJWTToken(jwt);
        const absoluteExpiration = Number(decodedToken?.[SessionService.ABSOLUTE_SESSION_EXPIRATION_CLAIM]);

        if (!Number.isFinite(absoluteExpiration)) {
            return true;
        }

        return absoluteExpiration <= Math.floor(Date.now() / 1000);
    }

    public getUserId(): string {
        const token = this.decodeJWTToken();
        return token ? token.userId || token.info.user_id : '';
    }

    private getUuid(): string {
        const token = this.decodeJWTToken();
        return token ? token?.info?.uuid || '' : '';
    }

    public isSuperAdmin(): boolean {
        const ADMIN_PERMISSION = 4096;
        const token = this.decodeJWTToken();
        return token ? (token.permissionLevel & ADMIN_PERMISSION) !== 0 : false;
    }

    public isExactSuperAdmin(): boolean {
        const EXACT_SUPERADMIN_PERMISSION = 2147483647;
        const token = this.decodeJWTToken();
        return token ? token.permissionLevel === EXACT_SUPERADMIN_PERMISSION : false;
    }

    public getCompany(): Promise<Dictionary<any>> {
        return this.integrationRestangular.one('companies').one(this.getUuid()).get().toPromise();
    }

    public getUser(): Promise<Dictionary<any>> {
        return this.integrationRestangular.one('users').one(this.getUserId()).get().toPromise();
    }

    createAccount(user: any): Promise<any> {
        return this.restangular.one('auth').all('create-user-password').post(user).toPromise();
    }

    async signUpAndLogin(user: any): Promise<any> {
        const createdUser = await this.createAccount(user);
        await this.loginUserPassword(user?.email || user?.username || '', user?.password || '');
        return createdUser;
    }

    getRandomPassword(): Promise<any> {
        return this.restangular.one('admin').one('generateInviteExample').get().toPromise().then((result: { randomInvite: any; }) => result.randomInvite);
    }

    async verifyEmailCode(email: string, code: string): Promise<any> {
        try {
            await this.cognitoMFAService.cognitoCustomChallengeAnswer(code);
            const data = await this.http.post<any>(
                `${AppSettings.getEndpoint()}auth/validate/login-without-pass/${code}`,
                { email },
                {
                    headers: { 'Content-Type': 'application/json' }
                }
            ).toPromise();
            this.setToken(data.id, data.refreshToken);
            await this.cognitoMFAService.saveCognitoRefreshTokenInformation();
            await this.navigateAfterLogin();
            return data;
        } catch (error) {
            console.error('Erro ao verificar código de email:', error);
            throw error;
        }
    }

    async getRefreshTokenCognito(): Promise<void> {
        try {
            const userId: string = this.getUserId();
            const jwtToken = this.tabStorageService.getStorage(this.JWT_TOKEN);
            const data = await this.http.get<any>(
                `${AppSettings.getEndpoint()}auth/refresh-cognito/${userId}`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${jwtToken}`,
                    }
                },
            ).toPromise();
            let accessToken = 'accessToken';
            let idToken = 'idToken';
            if (data?.refreshToken && data?.deviceKey) {
                const response = await this.cognitoMFAService.getAccessTokenUsingRefreshToken(
                    data.refreshToken,
                    data.deviceKey,
                );
                accessToken = response?.accessToken ?? accessToken;
                idToken = response?.idToken ?? idToken;
            }
            this.tabStorageService.setStorage(
                'CognitoIdentityServiceProvider.accessToken',
                accessToken,
            );
            this.tabStorageService.setStorage(
                'CognitoIdentityServiceProvider.idToken',
                idToken,
            );
        } catch (error) {
            console.log('Error while getting cognito refresh token:', error);
            throw error;
        }
    }
}
